import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Modal, Select, Space, Typography } from 'antd';
import { AppstoreOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import ParamListView from '../../../components/ParamListView';
import {
  getAllRoles,
  getAvailableCollections,
  getFieldConfigSeedStatus,
  seedFieldConfig,
} from '../../../services/adminApi';
import RolePermPanel from './components/RolePermPanel';
import ModuleFieldConfigEditor from './components/ModuleFieldConfigEditor';
import '../../../styles/UserSettingsSection.css';

const { Text } = Typography;

const CONFIG_TYPES = [
  { key: 'listView',    label: 'List View'   },
  { key: 'filter',      label: 'Filter'      },
  { key: 'form',        label: 'Form'        },
  { key: 'permissions', label: 'Permissions' },
];

function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function normalizeSeedKey(value) {
  return String(value || '').trim().toLowerCase();
}

function seedOptionValue(item) {
  return item?.key || item?.collectionName || item?.value || '';
}

function buildSeedOptions(statusModules = [], collections = []) {
  const options = [];
  const seenValues = new Set();
  const seededKeys = new Set();
  const moduleCollections = new Set();

  statusModules.forEach((module) => {
    const value = seedOptionValue(module);
    if (!value) return;

    const key = normalizeSeedKey(module.key || value);
    const collectionKey = normalizeSeedKey(module.collectionName);
    if (module.seeded) {
      seededKeys.add(key);
      if (collectionKey) seededKeys.add(collectionKey);
    }
    if (collectionKey) moduleCollections.add(collectionKey);

    seenValues.add(value);
    options.push({
      label: module.label || moduleTitle(value),
      value,
      disabled: Boolean(module.seeded),
    });
  });

  collections.forEach((collection) => {
    const value = String(collection || '').trim();
    if (!value || seenValues.has(value)) return;

    const collectionKey = normalizeSeedKey(value);
    if (moduleCollections.has(collectionKey) || seededKeys.has(collectionKey)) return;

    seenValues.add(value);
    options.push({
      label: moduleTitle(value),
      value,
      disabled: false,
    });
  });

  return options;
}

export default function RolesSection() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [modalOpen, setModalOpen]       = useState(false);
  const [configType, setConfigType]     = useState('listView');
  const [roles, setRoles]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [seedOptions, setSeedOptions]   = useState([]);
  const [seedModules, setSeedModules]   = useState([]);
  const [seedLoading, setSeedLoading]   = useState(false);
  const [seeding, setSeeding]           = useState(false);

  const availableSeedOptions = useMemo(
    () => seedOptions.filter((option) => !option.disabled),
    [seedOptions]
  );
  const allSeeded = !seedLoading && availableSeedOptions.length === 0;

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllRoles();
      setRoles(result.roles);
      setTotal(result.total);
    } catch {
      message.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSeedState = useCallback(async () => {
    setSeedLoading(true);
    try {
      const [status, collections] = await Promise.all([
        getFieldConfigSeedStatus(),
        getAvailableCollections(),
      ]);
      const options = buildSeedOptions(status.modules, collections);
      const allowed = new Set(options.filter((option) => !option.disabled).map((option) => option.value));
      setSeedOptions(options);
      setSeedModules((prev) => prev.filter((value) => allowed.has(value)));
    } catch {
      message.error('Failed to load seed status');
    } finally {
      setSeedLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);
  useEffect(() => { loadSeedState(); }, [loadSeedState]);

  async function handleSeedFieldConfig() {
    if (seedModules.length === 0) {
      message.warning(allSeeded ? 'All modules already seeded.' : 'Select at least one module to seed.');
      return;
    }

    setSeeding(true);
    try {
      await seedFieldConfig(seedModules);
      message.success('Role field config seeded');
      setSeedModules([]);
      await loadSeedState();
    } catch (error) {
      message.error(error?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  }

  function openConfig(role, tab = 'listView') {
    setSelectedRole(role);
    setConfigType(tab);
    setModalOpen(true);
  }

  const fields = [
    { label: 'Role Name',   value: 'roleName',   width: 200 },
    { label: 'Description', value: 'description', width: 240 },
    { label: 'Users',       value: 'userCount',   width: 70, sorter: false },
    {
      label: 'Actions', value: '_configure', width: 200, sorter: false,
      render: (_, record) => (
        <Space size={2}>
          <Button size="small" type="link" onClick={() => openConfig(record)}>Configure</Button>
          <Button
            size="small"
            type="link"
            icon={<LockOutlined />}
            onClick={() => openConfig(record, 'permissions')}
          >
            Permissions
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <AppCard
        icon={<AppstoreOutlined />}
        title="Field Config Seed"
        style={{ marginBottom: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 320, flex: 1 }}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              value={seedModules}
              options={seedOptions}
              placeholder={allSeeded ? 'All modules already seeded' : 'Select modules or collections to seed'}
              disabled={seedLoading || seeding || allSeeded}
              loading={seedLoading}
              onChange={setSeedModules}
              style={{ width: '100%' }}
            />
          </div>
          <Button
            type="primary"
            loading={seeding}
            disabled={seedLoading || seedModules.length === 0}
            onClick={handleSeedFieldConfig}
          >
            Seed Field Config
          </Button>
        </div>
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          {allSeeded
            ? 'Seed is disabled because all configured modules already have role field config data.'
            : 'Already seeded modules are disabled. New modules or collections can still be seeded.'}
        </Text>
      </AppCard>
      <AppCard icon={<SafetyOutlined />} title="Roles"
        style={{ marginBottom: 0 }}
        bodyStyle={{ padding: 0 }}>
        <ParamListView
          listName="Roles"
          dataSource={roles}
          fields={fields}
          loading={loading}
          total={total}
        />
      </AppCard>

      <Modal
        className="uss-modal"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyOutlined style={{ color: 'var(--color-brand)' }} />
            <span>Configure: {selectedRole?.roleName}</span>
          </div>
        }
        width={720} footer={null} destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <div className="uss-config-toggle">
            {CONFIG_TYPES.map((ct) => (
              <button key={ct.key}
                className={`uss-config-btn${configType === ct.key ? ' active' : ''}`}
                onClick={() => setConfigType(ct.key)}>{ct.label}</button>
            ))}
          </div>
        </div>
        {selectedRole && (
          configType === 'permissions' ? (
            <RolePermPanel key={selectedRole?.roleId} role={selectedRole} />
          ) : (
            <ModuleFieldConfigEditor
              key={`${selectedRole?.roleId}-${configType}`}
              role={selectedRole}
              configType={configType}
            />
          )
        )}
      </Modal>
    </>
  );
}
